import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_VIDEO_VOLUME } from '../config/videoPlayer.config';

/**
 * Keeps this video's mute and volume settings in sync with the shared audio selection.
 *
 * The hook lets the user mute, unmute, and change volume, writes those values to the
 * video element while it is playing, and tells the parent grid which video should be
 * the only unmuted audio source.
 */
const useVideoAudioSelection = ({
  unmutedVideoIndex, // Index of the video that is unmuted (user can have mutliple videos displayed at once)
  videoIndex, // Index of the video that is calling this hook
  setUnmutedVideoIndex, // Setter for setting a new unmuted video index
  videoElementKey,
  hasActiveStream, // Whether the video that is calling this hook is playing or not
  videoRef, // The HTML <video> element
}) => {
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(DEFAULT_VIDEO_VOLUME);

  const toggleMuted = useCallback(() => {
    setMuted((currentMuted) => {
      if (currentMuted && volume === 0) {
        setVolume(DEFAULT_VIDEO_VOLUME);
      }

      return !currentMuted;
    });
  }, [volume]);

  // Updates the volume and muted state when the user changes the volume slider
  const changeVolumeFromSlider = useCallback((event) => {
    const nextVolume = Number.parseFloat(event.target.value);
    if (!Number.isFinite(nextVolume)) return;

    setVolume(nextVolume);
    setMuted(nextVolume === 0);
  }, []);

  // Manually sets the video's muted and volume properties when the video element changes or when the user changes the muted/volume state
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasActiveStream) return;

    video.muted = muted;
    video.volume = volume;
  }, [videoElementKey, muted, hasActiveStream, videoRef, volume]);

  // Creates an event listener for when the user changes the volume or mutes/unmutes the video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const setActiveAudioFromVideo = () => {
      // If the video is unmuted, set it as the active audio source
      if (!video.muted) {
        setUnmutedVideoIndex(videoIndex);
        return;
      }

      // If the video is muted and it was the unmuted video, reset the unmuted video index
      if (videoIndex === unmutedVideoIndex) {
        setUnmutedVideoIndex(-1);
      }
    };

    video.addEventListener('volumechange', setActiveAudioFromVideo);
    return () => video.removeEventListener('volumechange', setActiveAudioFromVideo);
  }, [setUnmutedVideoIndex, unmutedVideoIndex, videoElementKey, videoIndex, videoRef]);

  // Ensures that the video is muted if it is not the unmuted video
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasActiveStream) return;

    setMuted(unmutedVideoIndex !== videoIndex);
  }, [unmutedVideoIndex, videoElementKey, hasActiveStream, videoIndex, videoRef]);

  return useMemo(
    () => ({
      muted,
      volume,
      toggleMuted,
      changeVolumeFromSlider,
    }),
    [changeVolumeFromSlider, muted, toggleMuted, volume],
  );
};

export default useVideoAudioSelection;
